import type { SectionKey } from '#/services/settings/spec.js'
import type { SvgChild } from './dom.js'
import { svgEl } from './dom.js'

// Outline (stroke-based) SVG icons for the Mini App — one per settings section, plus the close glyph (the
// per-section guide button is a plain bold "?" text, not an SVG). Style: 24×24 viewBox, 1.75 stroke, round
// caps/joins, currentColor (so CSS `color` tints them).
// They REPLACE the old emoji section prefixes (labels.ts) — an elegant, professional visual anchor that an
// emoji can't be. Path data is hand-written in the common geometric idiom (Lucide-like); section icons are
// decorative (aria-hidden — the title text carries the meaning). TYPE-only spec import → bundle-safe.

// Shared <svg> attributes. aria-hidden by default: these sit beside a text label that conveys the meaning;
// the help button overrides nothing (it carries its own aria-label on the <button>, icon stays decorative).
const BASE: Record<string, string> = {
  'viewBox': '0 0 24 24',
  'fill': 'none',
  'stroke': 'currentColor',
  'stroke-width': '1.75',
  'stroke-linecap': 'round',
  'stroke-linejoin': 'round',
  'aria-hidden': 'true',
}

// Section glyphs, keyed by the spec's SectionKey (so a typo or a new/removed section is caught by tsc —
// Record<SectionKey, …> must be exhaustive). flood→waves, captcha→verified user, warns→alert triangle,
// locks→padlock, raid→shield, filters→funnel, cleanup→sparkles, trusted→badge-check, rules→file-text,
// nightmode→crescent moon, aishield→bot (a robot head — the AI classifier; the "shield" meaning is in the
// title, and it stays distinct from raid's plain shield).
const SECTION_ICONS: Record<SectionKey, SvgChild[]> = {
  flood: [
    ['path', { d: 'M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1' }],
    ['path', { d: 'M2 12c.6.5 1.2 1 2.5 1C7 13 7 11 9.5 11c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1' }],
    ['path', { d: 'M2 18c.6.5 1.2 1 2.5 1C7 19 7 17 9.5 17c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1' }],
  ],
  captcha: [
    ['path', { d: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' }],
    ['circle', { cx: '9', cy: '7', r: '4' }],
    ['path', { d: 'm16 11 2 2 4-4' }],
  ],
  warns: [
    ['path', { d: 'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z' }],
    ['path', { d: 'M12 9v4' }],
    ['path', { d: 'M12 17h.01' }],
  ],
  locks: [
    ['rect', { width: '18', height: '11', x: '3', y: '11', rx: '2', ry: '2' }],
    ['path', { d: 'M7 11V7a5 5 0 0 1 10 0v4' }],
  ],
  raid: [
    ['path', { d: 'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z' }],
  ],
  filters: [
    ['polygon', { points: '22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3' }],
  ],
  cleanup: [
    ['path', { d: 'M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z' }],
    ['path', { d: 'M20 3v4' }],
    ['path', { d: 'M22 5h-4' }],
    ['path', { d: 'M4 17v2' }],
    ['path', { d: 'M5 18H3' }],
  ],
  trusted: [
    ['path', { d: 'M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z' }],
    ['path', { d: 'm9 12 2 2 4-4' }],
  ],
  rules: [
    ['path', { d: 'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z' }],
    ['path', { d: 'M14 2v4a2 2 0 0 0 2 2h4' }],
    ['path', { d: 'M10 9H8' }],
    ['path', { d: 'M16 13H8' }],
    ['path', { d: 'M16 17H8' }],
  ],
  nightmode: [
    ['path', { d: 'M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z' }],
  ],
  aishield: [
    ['path', { d: 'M12 8V4H8' }],
    ['rect', { width: '16', height: '12', x: '4', y: '8', rx: '2' }],
    ['path', { d: 'M2 14h2' }],
    ['path', { d: 'M20 14h2' }],
    ['path', { d: 'M15 13v2' }],
    ['path', { d: 'M9 13v2' }],
  ],
}

// An X for the sheet's close button. (The per-section guide trigger is now a plain bold "?" text, not an SVG.)
const CLOSE_ICON: SvgChild[] = [
  ['path', { d: 'M18 6 6 18' }],
  ['path', { d: 'm6 6 12 12' }],
]

// A column chart on an axis — the stats section's anchor (read-only counts, not a settings section, so
// not a SectionKey; its own export like STATS_ICON's siblings below).
const STATS_ICON: SvgChild[] = [
  ['path', { d: 'M3 3v16a2 2 0 0 0 2 2h16' }],
  ['path', { d: 'M8 17v-3' }],
  ['path', { d: 'M13 17V9' }],
  ['path', { d: 'M18 17V5' }],
]

// A megaphone — the log-channel section's anchor (moderation events are broadcast/mirrored to a channel).
// Not a SectionKey (bespoke control, like silent/stats), so its own export.
const LOG_CHANNEL_ICON: SvgChild[] = [
  ['path', { d: 'm3 11 18-5v12L3 14v-3z' }],
  ['path', { d: 'M11.6 16.8a3 3 0 1 1-5.8-1.6' }],
]

// Horizontal sliders — the General panel's anchor (the group-wide silent-mode preference). Not a
// SectionKey (a bespoke panel, like stats/log-channel), so its own export.
const GENERAL_ICON: SvgChild[] = [
  ['line', { x1: '21', y1: '4', x2: '14', y2: '4' }],
  ['line', { x1: '10', y1: '4', x2: '3', y2: '4' }],
  ['line', { x1: '21', y1: '12', x2: '12', y2: '12' }],
  ['line', { x1: '8', y1: '12', x2: '3', y2: '12' }],
  ['line', { x1: '21', y1: '20', x2: '16', y2: '20' }],
  ['line', { x1: '12', y1: '20', x2: '3', y2: '20' }],
  ['line', { x1: '14', y1: '2', x2: '14', y2: '6' }],
  ['line', { x1: '8', y1: '10', x2: '8', y2: '14' }],
  ['line', { x1: '16', y1: '18', x2: '16', y2: '22' }],
]

// A right-pointing chevron — the "this row opens something" affordance on the tappable AI-Shield
// activity rows (the drill-down). Decorative (aria-hidden via BASE): the row's own text is the label.
const CHEVRON_ICON: SvgChild[] = [
  ['path', { d: 'm9 6 6 6-6 6' }],
]

/** The section's outline icon as an <svg> with the given class (color comes from CSS `color`). */
export function sectionIcon(key: SectionKey, className: string): SVGSVGElement {
  return svgEl({ ...BASE, class: className }, SECTION_ICONS[key])
}

/** The tappable-row chevron (a right arrowhead). */
export function chevronIcon(className: string): SVGSVGElement {
  return svgEl({ ...BASE, class: className }, CHEVRON_ICON)
}

/** The sheet close glyph (an "×"). */
export function closeIcon(className: string): SVGSVGElement {
  return svgEl({ ...BASE, class: className }, CLOSE_ICON)
}

/** The stats glyph (a column chart) — the stats section head + its info sheet. */
export function statsIcon(className: string): SVGSVGElement {
  return svgEl({ ...BASE, class: className }, STATS_ICON)
}

/** The log-channel glyph (a megaphone) — the log-channel section head + its info sheet. */
export function logChannelIcon(className: string): SVGSVGElement {
  return svgEl({ ...BASE, class: className }, LOG_CHANNEL_ICON)
}

/** The General-panel glyph (horizontal sliders) — the silent-mode panel head. */
export function generalIcon(className: string): SVGSVGElement {
  return svgEl({ ...BASE, class: className }, GENERAL_ICON)
}

/**
 * The AI-Shield glyph (the robot head) — the read-only AI-Shield activity section head + its info sheet.
 * REUSES the aishield SECTION_ICON path so the results block visually echoes the AI Shield settings section
 * (one glyph, no divergence). It's a bespoke read-only block (like stats/log-channel), not a SectionKey, so
 * it needs its own factory rather than going through sectionIcon.
 */
export function aiShieldIcon(className: string): SVGSVGElement {
  return svgEl({ ...BASE, class: className }, SECTION_ICONS.aishield)
}
